import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Invoice } from '~/api/invoices/types';

import { HttpBusinessConflictError, HttpNotFoundError } from '~/api/errors';
import { useAcquireInvoiceLock, useDeleteInvoice, useDownloadInvoiceFcFiles, useDuplicateInvoice, useViewInvoice } from '~/api/invoices';
import { useResendCoInvoice } from '~/api/invoices/useResendCoInvoice';
import { AlertDialog } from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';

export const useOnShowInvoice = (invoice: Invoice) => {
  const navigate = useNavigate();

  const onShow = useCallback(() => navigate(r(routes.showInvoice, { invoiceId: invoice.id })), [invoice.id, navigate]);

  return useMemo(() => ({ onShow }), [onShow]);
};

export const useOnEditInvoice = (invoice: Invoice) => {
  const { isPending, mutate } = useAcquireInvoiceLock(invoice.id);

  const confirmEditHandle = AlertDialog.useHandle();
  const alreadyApprovedHandle = AlertDialog.useHandle();

  const navigate = useNavigate();
  const { t } = useTranslation(['invoices']);

  const onEdit = useCallback(() => {
    if (invoice.status === 'Draft') return navigate(r(routes.createInvoice, { invoiceId: invoice.id }));

    confirmEditHandle.open(null);
  }, [confirmEditHandle, invoice.id, invoice.status, navigate]);

  const confirmEdit = useCallback(() => {
    mutate(undefined, {
      onSuccess: () => navigate(r(routes.editInvoice, { invoiceId: invoice.id })),
      onError: (error) => {
        if (error instanceof HttpBusinessConflictError && error.errorCode === 'ApprovedInvoiceCannotBeLockedByUser') {
          return alreadyApprovedHandle.open(null);
        }

        return toast.danger({ description: t('invoices:overview.rowActions.edit.error') });
      },
    });
  }, [alreadyApprovedHandle, invoice.id, mutate, navigate, t]);

  return useMemo(
    () => ({ onEdit, confirmEdit, isPending, confirmEditHandle, alreadyApprovedHandle }),
    [alreadyApprovedHandle, confirmEdit, confirmEditHandle, isPending, onEdit],
  );
};

export const useOnViewInvoice = (invoice: Invoice) => {
  const { data, isPending, mutate, reset } = useViewInvoice(invoice.id);

  const { t } = useTranslation(['common']);

  const onPreview = useCallback(
    () =>
      mutate(undefined, {
        onError: () => toast.danger({ description: t('common:error') }),
      }),
    [mutate, t],
  );

  return useMemo(() => ({ onPreview, isPending, data, reset }), [onPreview, isPending, data, reset]);
};

export const useOnDuplicateInvoice = (invoice: Invoice) => {
  const { isPending, mutate } = useDuplicateInvoice(invoice.id);

  const navigate = useNavigate();
  const { t } = useTranslation(['common']);

  const onDuplicate = useCallback(
    () =>
      mutate(undefined, {
        onSuccess: ({ id }) => navigate(r(routes.createInvoice, { invoiceId: id })),
        onError: () => toast.danger({ description: t('common:error') }),
      }),
    [mutate, navigate, t],
  );

  return useMemo(() => ({ onDuplicate, isPending }), [isPending, onDuplicate]);
};

export const useOnDownloadFcInvoice = (invoice: Invoice) => {
  const { isPending, mutate } = useDownloadInvoiceFcFiles();

  const [showConfirmDownloadFcInvoiceModal, setShowConfirmDownloadFcInvoiceModal] = useState(true);

  const downloadHandle = AlertDialog.useHandle();

  const { t } = useTranslation(['common', 'invoices']);

  const confirmDownload = useCallback(() => {
    mutate(
      { ids: [invoice.id] },
      {
        onSuccess: () => downloadHandle.close(),
        onError: (error) =>
          toast.danger({
            description: error instanceof HttpNotFoundError ? t('invoices:alerts.missingFcFiles', { count: 1 }) : t('common:error'),
          }),
      },
    );
  }, [downloadHandle, invoice.id, mutate, t]);

  const onDownload = useCallback(() => {
    if (showConfirmDownloadFcInvoiceModal) {
      setShowConfirmDownloadFcInvoiceModal(false);
      return downloadHandle.open(null);
    }

    confirmDownload();
  }, [confirmDownload, downloadHandle, showConfirmDownloadFcInvoiceModal]);

  return useMemo(
    () => ({ onDownload, confirmDownload, isPending, downloadHandle }),
    [confirmDownload, downloadHandle, isPending, onDownload],
  );
};

export const useOnResendCoInvoice = (invoice: Invoice) => {
  const resendHandle = AlertDialog.useHandle();
  const { isPending, mutate } = useResendCoInvoice(invoice.id);
  const { t } = useTranslation(['common', 'invoices']);

  const onResend = useCallback(() => resendHandle.open(null), [resendHandle]);

  const confirmResend = useCallback(() => {
    mutate(undefined, {
      onSuccess: () => {
        resendHandle.close();
        toast.success({ description: t('invoices:alerts.successResendCoInvoice') });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [mutate, resendHandle, t]);

  return useMemo(() => ({ onResend, confirmResend, isPending, resendHandle }), [confirmResend, isPending, onResend, resendHandle]);
};

export const useOnCreditInvoice = (invoice: Invoice) => {
  const navigate = useNavigate();

  const onCredit = useCallback(
    () => navigate({ pathname: routes.createCreditNote, search: qs({ [SearchParamKeys.INVOICE_ID]: invoice.id }) }),
    [invoice.id, navigate],
  );

  return useMemo(() => ({ onCredit }), [onCredit]);
};

export const useOnDeleteInvoice = (invoice: Invoice) => {
  const { isPending, mutate } = useDeleteInvoice(invoice.id);

  const deleteHandle = AlertDialog.useHandle();

  const { t } = useTranslation(['common', 'invoices']);

  const onDelete = useCallback(() => deleteHandle.open(null), [deleteHandle]);

  const confirmDelete = useCallback(() => {
    mutate(undefined, {
      onSuccess: () => {
        deleteHandle.close();
        toast.success({ description: t('invoices:alerts.successDeleted') });
      },
      onError: () => toast.danger({ description: t('common:error') }),
    });
  }, [deleteHandle, mutate, t]);

  return useMemo(() => ({ onDelete, confirmDelete, isPending, deleteHandle }), [confirmDelete, deleteHandle, isPending, onDelete]);
};
